import { Component, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigService } from '@app/core/config/config.service';
import { AuthenticationInfoService } from '@app/core/guards/authentication-info.service';
import { ImportReportModel } from '@app/core/models/administration/imports/import-report.model';
import { GroupSearchCriteriaModel } from '@app/core/models/groups/group-search-criteria.model';
import { GroupTypeModel } from '@app/core/models/groups/group-type.model';
import { GroupModel } from '@app/core/models/groups/group.model';
import { PagedResult } from '@app/core/models/paged-result.model';
import { ReferentialModel } from '@app/core/models/referentials/referential.model';
import { ReferentialsModel } from '@app/core/models/referentials/referentials.model';
import { SearchFilterModel } from '@app/core/models/search-filter.model';
import { ReferentialItem } from '@app/core/models/shared/referential-item.model';
import { GroupService } from '@app/core/services/group.service';
import { SessionStorageService } from '@app/core/services/session-storage.service';
import { AdministrationBase } from '@app/features/administration/administration-base';
import { DropdownHelper } from '@app/shared/models/dropdown-helper.model';
import { ModalHelper } from '@app/shared/models/modal-helper.model';
import { MultiSelectModel } from '@app/shared/models/multi-select.model';
import { TableActionIcon, TableRowAction } from '@app/shared/models/table-action-type.model';
import { TableNavigationModel } from '@app/shared/models/table-navigation.model';
import * as _ from 'lodash';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { GroupTransferUsersComponent } from '../group-transfer-users/group-transfer-users.component';

@Component({
  selector: 'app-group-list',
  templateUrl: './group-list.component.html',
  styleUrls: ['./group-list.component.scss'],
})
export class GroupListComponent
  extends AdministrationBase<GroupModel>
  implements OnInit, OnDestroy
{
  @Input() onExportDataSourceAvailable: EventEmitter<any> =
    new EventEmitter<any>();
  paginatorLength: number;
  sortColumn: string = '';
  sortDirection: string = '';
  pageIndex: number = 0;
  pageSize: number = 20;

  dropdownListIsActive: ReferentialItem[] = [];
  selectedItemsIsActive: ReferentialItem[] = [];
  dropdownListGroupTypes: ReferentialItem[] = [];
  selectedItemsGroupTypes: ReferentialItem[] = [];
  dropdownListIsVoucher: ReferentialItem[] = [];
  selectedItemsIsVoucher: ReferentialItem[] = [];
  dropdownSettings: IDropdownSettings;
  filterStorageKey: string;

  panelOpenState: boolean = true;

  private groupNameInputChanged$: Subject<void> = new Subject<void>();
  groupSearchCriteriaModel: GroupSearchCriteriaModel;

  constructor(
    route: ActivatedRoute,
    router: Router,
    dialog: MatDialog,
    private config: ConfigService,
    authenticationInfoService: AuthenticationInfoService,
    private dropdownHelper: DropdownHelper,
    private multiSelectModel: MultiSelectModel,
    private sessionStorageService: SessionStorageService,
    private groupService: GroupService
  ) {
    super(dialog, router, route, authenticationInfoService);
    this.dataItems = [];
    this.groupSearchCriteriaModel = new GroupSearchCriteriaModel();
    this.title = 'GROUPES';
    this.subtitle = 'groupe';
  }

  ngOnInit(): void {
    this.filterStorageKey = this.route.snapshot.data.searchFilterKey;

    this.route.data.subscribe((data: any) => {
      const referentials: ReferentialsModel = data.referentials;
      this.initDropdownLists(referentials.GroupTypes);
    });
    this.groupNameInputChanged$
      .pipe(takeUntil(this.unsubscribe$), debounceTime(250))
      .subscribe(() => {
        this.loadGroups();
      });

    this.restoreFilters();

    this.loadGroups();
    this.tableActions.push(
      new TableRowAction(
        TableActionIcon.FORWARD,
        'Transférer les utilisateurs',
        this.onForward,
        this.canForward,
        null,
        !this.isAdministrator
      )
    );
  }

  initDropdownLists(groupTypes: ReferentialModel[]): void {
    this.dropdownSettings = this.multiSelectModel.getMultipleSelection(true);
    this.dropdownListIsActive = this.dropdownHelper.getYesOrNoDropdownList();
    this.dropdownListIsVoucher =
      this.dropdownHelper.getYesOrNoOrNaDropdownList();
    this.dropdownListGroupTypes = groupTypes
      .filter((groupTypes: ReferentialModel) => groupTypes.Id > 1)
      .map(
        (groupType: ReferentialModel) =>
          new ReferentialItem(groupType.Id, groupType.Label)
      );
  }

  canForward = (group: GroupModel): boolean => {
    return (
      _.isNil(this.authenticationInfoService?.authentificatedUser) ||
      !this.authenticationInfoService.authentificatedUser.hasGroup(group.Id)
    );
  };

  protected activateItem(id: any): Observable<any> {
    return this.groupService.activateGroup(id);
  }

  protected deactivateItem(id: any): Observable<any> {
    return this.groupService.deactivateGroup(id);
  }

  onForward = (group: GroupModel) => {
    const transfertModal = new ModalHelper().getTransferUsersModalConfig(
      group.Id.toString(),
      group.Name
    );
    this.dialog.open(GroupTransferUsersComponent, transfertModal);
  };

  protected getDataItems(): Observable<any> {
    this.pageIndex = 0;
    return this.groupService.searchGroups(
      this.sortColumn,
      this.sortDirection,
      this.pageIndex,
      this.pageSize
    );
  }

  protected extractItemsFromData(data: any): GroupModel[] {
    return data.Items;
  }

  protected onDataFetched(data: any): void {
    this.paginatorLength = data.TotalItems;
  }

  importHandler = (files: File[]): Observable<ImportReportModel[]> => {
    return this.groupService.importGroups(files);
  };

  onPageChange = (page: PageEvent) => {
    this.pageIndex = page.pageIndex;
    this.pageSize = page.pageSize;
    this.loadGroups();
  };

  onSortChange = (sort: Sort) => {
    this.sortColumn = sort.active;
    this.sortDirection = sort.direction;
    this.pageIndex = 0;
    this.loadGroups();
  };

  onClearFilters = (): void => {
    this.onFiltersCleared.next();
    this.clearFilters();
  };

  clearFilters = (): void => {
    this.groupSearchCriteriaModel = new GroupSearchCriteriaModel();
    this.pageIndex = 0;
    this.selectedItemsGroupTypes = [];
    this.selectedItemsIsActive = [];
    this.selectedItemsIsVoucher = [];
    this.onItemSelect();
  };

  onSelectAll(items: any, filter: string): void {
    switch (filter) {
      case 'Type':
        this.selectedItemsGroupTypes = items;
        break;
      case 'IsActive':
        this.selectedItemsIsActive = items;
        break;
      case 'IsVoucher':
        this.selectedItemsIsVoucher = items;
        break;
    }
    this.onItemSelect();
  }

  onItemSelect(): void {
    this.groupSearchCriteriaModel.GroupTypes =
      this.selectedItemsGroupTypes && this.selectedItemsGroupTypes.length >= 1
        ? this.selectedItemsGroupTypes.map((a) => a.Id as GroupTypeModel)
        : null;

    this.groupSearchCriteriaModel.IsActive =
      this.selectedItemsIsActive && this.selectedItemsIsActive.length == 1
        ? this.selectedItemsIsActive[0].Label == 'Oui'
          ? true
          : false
        : null;

    this.groupSearchCriteriaModel.IsVoucher =
      this.selectedItemsIsVoucher && this.selectedItemsIsVoucher.length >= 1
        ? this.selectedItemsIsVoucher
            .map((x) => x['Id'])
            .reduce((x, y) => x + y)
        : null;

    this.groupSearchCriteriaModel.GroupTypes = this.selectedItemsGroupTypes.map(
      (i) => i.Id
    );
    this.loadGroups();
  }

  inputChanged(): void {
    this.groupNameInputChanged$.next();
  }

  getGroupsForExport = () => {
    this.groupService
      .searchGroups(
        this.sortColumn,
        this.sortDirection,
        0,
        this.config.environnement.maxExportRows,
        this.groupSearchCriteriaModel
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((pagedResult: PagedResult<GroupModel>) => {
        this.onExportDataSourceAvailable.emit(pagedResult.Items);
      });
  };

  private loadGroups(): void {
    this.groupService
      .searchGroups(
        this.sortColumn,
        this.sortDirection,
        this.pageIndex,
        this.pageSize,
        this.groupSearchCriteriaModel
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: PagedResult<GroupModel>) => {
        this.dataItems = data.Items;
        this.paginatorLength = data.TotalItems;
        this.storeFilters();
      });
  }

  private restoreFilters(): void {
    const searchFilter = this.sessionStorageService.get<
      SearchFilterModel<GroupSearchCriteriaModel>
    >(this.filterStorageKey);
    const pageNavigation = Object.assign(
      {},
      JSON.parse(localStorage.getItem('table_config_group'))?.pageDef
    ) as TableNavigationModel;

    if (!_.isNil(pageNavigation.Sort)) {
      this.sortColumn = pageNavigation.Sort.active;
      this.sortDirection = pageNavigation.Sort.direction;
    }

    if (!_.isNil(pageNavigation.Page)) {
      this.pageSize = pageNavigation.Page.pageSize;
    }

    if (_.isNil(searchFilter)) {
      return;
    }

    this.groupSearchCriteriaModel = searchFilter.Filter;
    this.pageIndex = searchFilter.Page;

    if (!_.isNil(searchFilter.Filter.GroupTypes)) {
      const selectedValues = searchFilter.Filter.GroupTypes;
      this.selectedItemsGroupTypes.push(
        ...this.dropdownListGroupTypes.filter((i) =>
          selectedValues.includes(i.Id)
        )
      );
    }

    if (!_.isNil(searchFilter.Filter.IsActive)) {
      const isActive = searchFilter.Filter.IsActive;
      this.selectedItemsIsActive.push(
        ...this.dropdownListIsActive.filter(
          (i) => (isActive && i.Id == 1) || (!isActive && i.Id == 0)
        )
      );
    }

    if (!_.isNil(searchFilter.Filter.IsVoucher)) {
      this.selectedItemsIsVoucher.push(
        ...this.dropdownListIsVoucher.filter(
          (i) => searchFilter.Filter.IsVoucher & i.Id
        )
      );
    }
  }

  private storeFilters(): void {
    var filter = new SearchFilterModel();
    filter.Filter = this.groupSearchCriteriaModel;
    filter.Page = this.pageIndex;
    sessionStorage.setItem(this.filterStorageKey, JSON.stringify(filter));
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }

  exportAsExcel(): void {
    this.onExportExcelCaller.next();
  }
}
